import ExcelJS from "exceljs";
import { ISetupCharge } from "../../../shared/services/api/setupCharge/Types";
import { SETUP_CHARGE_REPORT_HEADER, setupChargeReportRow, setupChargeToNumber } from "./setupChargeFormat";

/** Colunas de valor (índice no cabeçalho do relatório): vão como número, para somar e filtrar na planilha. */
const MONEY_COLUMNS = new Map<number, (charge: ISetupCharge) => number | undefined>([
	[6, (charge) => setupChargeToNumber(charge.amount)],
	[7, (charge) => setupChargeToNumber(charge.gatewayFee)],
	[8, (charge) => setupChargeToNumber(charge.netAmount)],
	[9, (charge) => setupChargeToNumber(charge.expectedRepass)],
	[10, (charge) => setupChargeToNumber(charge.repassAmount)],
]);

const MONEY_FORMAT = '"R$" #,##0.00';

/** Linha do relatório com os valores em reais como número (o resto continua em texto, igual ao CSV). */
export const setupChargeXlsxRow = (charge: ISetupCharge): (string | number | null)[] =>
	setupChargeReportRow(charge).map((text, index) => {
		const money = MONEY_COLUMNS.get(index);
		if (!money) return text;
		return money(charge) ?? null;
	});

export const buildSetupChargeXlsx = async (charges: ISetupCharge[]): Promise<ArrayBuffer> => {
	const workbook = new ExcelJS.Workbook();
	const sheet = workbook.addWorksheet("Taxa Setup Avulsa", { views: [{ state: "frozen", ySplit: 1 }] });

	sheet.addRow(SETUP_CHARGE_REPORT_HEADER);
	charges.forEach((charge) => sheet.addRow(setupChargeXlsxRow(charge)));

	const header = sheet.getRow(1);
	header.font = { bold: true, color: { argb: "FFFFFFFF" } };
	header.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
	header.eachCell((cell) => {
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0078D7" } };
	});

	SETUP_CHARGE_REPORT_HEADER.forEach((label, index) => {
		const column = sheet.getColumn(index + 1);
		if (MONEY_COLUMNS.has(index)) column.numFmt = MONEY_FORMAT;
		const longest = charges.reduce((max, charge) => Math.max(max, String(setupChargeReportRow(charge)[index] ?? "").length), label.length);
		column.width = Math.min(longest + 2, 45);
	});
	header.eachCell((cell) => {
		// o formato da coluna não deve valer para o texto do cabeçalho
		cell.numFmt = "General";
	});

	return workbook.xlsx.writeBuffer() as Promise<ArrayBuffer>;
};

export const downloadSetupChargeXlsx = async (charges: ISetupCharge[], fileName: string): Promise<void> => {
	const buffer = await buildSetupChargeXlsx(charges);
	const url = URL.createObjectURL(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};
